import { type RefObject, useLayoutEffect, useState } from 'react';
import { clamp, isEqual } from 'lodash';

import { instantOf } from '../../ActionHistory/utils';
import { logEntryElementId } from '../../utils';
import type { LogEntryModel } from '../types';

import type { StageChapter } from './types';

/**
 * How far down the log each chapter's entry is, by the chapter's index, from 0 at the top of all the log scrolls to 1
 * at its foot: the entry's top within it, over its whole height, wherever the reader has scrolled it to. The entry is
 * found in the DOM, as the entries range from a line to a card with a table. A move after every entry the log draws is
 * just past the newest of them, below it or, once the log is reversed, above it, so a log shorter than its view does not
 * draw it at the foot of the view; one older than all of them is `undefined`, as its entries are still to be read in.
 *
 * Read again when the chapters or the entries change, or the log is reversed, before the browser paints, and whenever
 * the log's view or the box of its entries changes size, as when an entry above a chapter's grows.
 */
export const useChapterOffsets = (
  scrollerRef: RefObject<HTMLElement>,
  /** The box the log draws its entries in. */
  contentRef: RefObject<HTMLElement>,
  /** The entries the log draws, in the order it draws them. */
  entries: LogEntryModel[],
  isReversed: boolean,
  chapters: StageChapter[],
  entryIdOf: (chapter: StageChapter) => string | undefined
): (number | undefined)[] => {
  const [offsets, setOffsets] = useState<(number | undefined)[]>([]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!scroller || !content || chapters.length === 0) return undefined;

    // Which entry a chapter is drawn at changes only with this effect's inputs, so a resize reads the boxes alone.
    const newest = entries[isReversed ? 0 : entries.length - 1];
    const anchors = chapters.map((chapter) => {
      const eventId = entryIdOf(chapter);
      if (eventId) return { eventId, isPastNewest: false };

      // No entry the log draws is at or after the move, though some are before it: it came after them all.
      return entries.some(({ notification }) => instantOf(notification.timestamp) <= chapter.changedAt)
        ? { eventId: newest.notification.event_id, isPastNewest: true }
        : undefined;
    });

    const measure = () => {
      const top = scroller.getBoundingClientRect().top - scroller.scrollTop;
      const height = Math.max(scroller.scrollHeight, 1);
      const next = anchors.map((anchor) => {
        const box = anchor && document.getElementById(logEntryElementId(anchor.eventId))?.getBoundingClientRect();
        if (!anchor || !box) return undefined;

        // Past the newest entry is below it, or above it once the log is reversed.
        return clamp(((anchor.isPastNewest && !isReversed ? box.bottom : box.top) - top) / height, 0, 1);
      });
      setOffsets((current) => (isEqual(current, next) ? current : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    observer.observe(content);

    return () => observer.disconnect();
  }, [scrollerRef, contentRef, entries, isReversed, chapters, entryIdOf]);

  return offsets;
};
